// @flow
import React, { PureComponent, Fragment } from 'react';
import type { Element } from 'react';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import classNames from 'classnames';

import { Bling as GPT } from 'react-gpt';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import FilterForm from 'Common/FilterForm';
import getFilterBreakdownProp from 'Common/getFilterBreakdownProp';
import getQueryString from 'Common/getQueryString';
import buildUrlFromForm from 'Common/buildUrlFromForm';
import {
  gtmBasicOptions,
  gtmFurtherOptions,
  gtmWinners,
  gtmPageview
} from 'Common/gtmPayloads';
import {
  slots,
  createAdUnitPath,
  targetingHome,
  adUnitPageNames
} from 'Common/ads';
import scrollToTopOfPage from 'Common/scrollToTopOfPage';
import isWindow from 'Common/isWindow';
import isWindowProp from 'Common/isWindowProp';
import { gtmEventPush } from 'utils/gtm/events';
import getClassifiedsSite from 'utils/getClassifiedsSite';
import { setAutocarHeaderVisibility } from 'utils/autocar';
import pageData from 'utils/pageData';

import * as Actions from 'actionTypes';
import metadata from 'App/variables/seo';

import { Grid, Row, Col } from 'Common/Grid';
import Accordion from 'Common/Accordion';
import BrowseAll from 'Common/BrowseAll';
import SEO from 'Common/Seo';
import ErrorBox from 'Common/ErrorBox';
import SearchForm from 'Common//SearchForm';
import CardsCarousel from 'Common/CardsCarousel';
import QuickLinks from 'Common/QuickLinks';

import styles from 'rootContainer/Home.scss';

const pageType = 'car-leasing-deals';
const thisPageData = pageData[pageType];
const homeText = thisPageData.homeText;

const quickLinksData = [
  {
    label: 'Electric deals',
    href:
      '/car-leasing-deals/all?standardFuelType=Electric&sort=leaseMonthlyPriceAsc'
  },
  {
    label: 'SUV',
    href: 'car-leasing-deals/suv?sort=leaseMonthlyPriceAsc&standardBodyType=SUV'
  },
  {
    label: 'Petrol hybrid',
    href:
      '/car-leasing-deals/all?standardFuelType=Petrol%20Hybrid&sort=leaseMonthlyPriceAsc'
  },
  {
    label: 'Plug in hybrid',
    href:
      '/car-leasing-deals/all?standardFuelType=Petrol%20Plug-in%20Hybrid&sort=leaseMonthlyPriceAsc'
  },
  {
    label: 'Under £400 p/m',
    href:
      '/car-leasing-deals/all?maxLeaseMonthlyPrice=400&sort=leaseMonthlyPriceAsc'
  },
  {
    label: 'Coupe',
    href:
      '/car-leasing-deals/coupe?standardBodyType=Coupe&sort=leaseMonthlyPriceAsc'
  },
  {
    label: 'Under £200 p/m',
    href:
      '/car-leasing-deals/all?sort=leaseMonthlyPriceAsc&maxLeaseMonthlyPrice=200'
  },
  {
    label: 'Convertible',
    href:
      '/car-leasing-deals/convertible?sort=leaseMonthlyPriceAsc&standardBodyType=Convertible'
  },
  {
    label: 'Estate',
    href:
      '/car-leasing-deals/estate?standardBodyType=Estate&sort=leaseMonthlyPriceAsc'
  },
  {
    label: 'MPV',
    href:
      '/car-leasing-deals/mpv?standardBodyType=MPV&sort=leaseMonthlyPriceAsc'
  },
  {
    label: 'Under £300 p/m',
    href:
      '/car-leasing-deals/all?sort=leaseMonthlyPriceAsc&maxLeaseMonthlyPrice=300'
  },
  {
    label: 'Hatchback',
    href:
      '/car-leasing-deals/hatchback?sort=leaseMonthlyPriceAsc&standardBodyType=Hatchback'
  },
  {
    label: 'Saloon',
    href:
      '/car-leasing-deals/saloon?standardBodyType=Saloon&sort=leaseMonthlyPriceAsc'
  },
  {
    label: 'Under £800 p/m',
    href:
      '/car-leasing-deals/all?sort=leaseMonthlyPriceAsc&maxLeaseMonthlyPrice=800'
  }
];

const createActiveFields = R.compose(
  R.keys,
  R.filter(R.identity),
  R.omit(['lat', 'long', 'nearlynew', 'used', 'sortBy'])
);

/**
 * <Home /> component.
 * @return {class} returns home instance
 */
export class Home extends PureComponent<*, *> {
  currentBounce: Function;
  debounce: Function;
  interval: any;

  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      showFurtherOptions: false,
      isError: false,
      delayAdMasthead: true,
      delayAdOverlay: true,
      delayAdOverlay2: true
    };
  }

  /**
   * MapStateToProps implementation.
   * @return {object} new state object
   * @param {object} state object
   */
  static mapStateToProps(state: Object): Object {
    /**
     * pluckFromState
     * @param {string} prop - prop
     * @param {string} key - key
     * @return {array} - options
     */
    const pluckFromState = (prop, key) =>
      getFilterBreakdownProp(state, prop, key);

    const additonalOptions = {
      fuelTypes: pluckFromState('fuelType'),
      category: pluckFromState('category'),
      colours: pluckFromState('colour'),
      doors: pluckFromState('doors'),
      seats: pluckFromState('no_of_seats'),
      transmissions: pluckFromState('transmission'),
      annualTax: pluckFromState('annualTax'),
      fuelConsumption: pluckFromState('ec_combined_mpg'),
      co2Emissions: pluckFromState('co2_gkm'),
      acceleration: pluckFromState('0_to_62_mph_secs'),
      engineSizeFrom: pluckFromState('badge_engine_ccFrom'),
      engineSizeTo: pluckFromState('badge_engine_ccTo')
    };

    return {
      makes: pluckFromState('make'),
      models: pluckFromState('model'),
      priceFrom: pluckFromState('cashPriceStat', 'min'),
      priceTo: pluckFromState('cashPriceStat', 'max'),
      additonalOptions,
      form: state.form.search,
      filterForm: state.form.search,
      ui: state.ui,
      results: state.results,
      isResultsLoading: state.results.isLoading,
      isUILoading: state.ui.isLoading,
      isError: state.ui.error,
      popularCars: state.popular.results,
      isMobile: state.viewport.isXs
    };
  }

  /* eslint-disable camelcase */
  /**
   * UNSAFE_componentWillMount it fetches the makes.
   * @return {void}
   */
  UNSAFE_componentWillMount() {
    if (!this.props.filterForm) {
      this.props.dispatch({
        type: Actions.FETCH_MAKES,
        currentUrl: '/car-leasing-deals'
      });
    }

    this.props.dispatch({
      type: Actions.FETCH_POPULAR_RESULTS,
      query: `pageSize=50`,
      currentUrl: '/car-leasing-deals'
    });
  }

  componentDidMount() {
    GPT.enableSingleRequest();
    targetingHome();

    this.interval = setInterval(this.pushPv, 300);

    if (this.props.form && this.props.form.values) {
      this.resetAllFields({ ...this.props.form.values });
    }

    // Delay loading of the top ad to prioritize side ad
    this.delayAdMastheadTimeout = setTimeout(() => {
      this.setState({ delayAdMasthead: false });
    }, 100); // Adjust delay as needed
    this.delayAdOverlayTimeout = setTimeout(() => {
      this.setState({ delayAdOverlay: false });
    }, 150); // Adjust delay as needed
    this.delayAdOverlay2Timeout = setTimeout(() => {
      this.setState({ delayAdOverlay2: false });
    }, 150); // Adjust delay as needed
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    if (isWindow() && isWindowProp('dataLayer')) {
      window.dataLayer.push({ page: '' });
    }

    // Clear timeout to avoid memory leaks
    clearTimeout(this.delayAdMastheadTimeout);
    clearTimeout(this.delayAdOverlayTimeout);
    clearTimeout(this.delayAdOverlay2Timeout);
  }

  pushPv: () => void = () => {
    if (isWindow() && isWindowProp('dataLayer') && isWindowProp('location')) {
      const pageViewData = {
        content_type: 'Leasing',
        navigation_section: 'Leasing deals'
      };
      window.dataLayer.push(gtmPageview(pageViewData));

      clearInterval(this.interval);
    }
  };

  submit: () => void = () => {
    if (
      this.props.isMobile &&
      typeof window !== 'undefined' &&
      window.document &&
      document.body
    ) {
      // on mobile, puts back the scroll after submiting the form from advanced filters
      document.body.style.overflow = 'auto';
      setAutocarHeaderVisibility(true);
    }
    if (isWindow() && isWindowProp('dataLayer')) {
      if (this.state.showFurtherOptions) {
        window.dataLayer.push(gtmFurtherOptions(this.props.form.values));
      } else {
        window.dataLayer.push(gtmBasicOptions(this.props.form.values));
      }
    }
    const url = buildUrlFromForm(this.props.form, '', thisPageData.name);
    this.props.history.push(url);
  };

  pushGtm: (tag: string, title: string) => void = (tag, title) => {
    if (isWindow() && isWindowProp('dataLayer')) {
      const titleAndYear = title.split('(');
      window.dataLayer.push(
        gtmWinners(tag, titleAndYear[0], titleAndYear[1].slice(0, -1))
      );
    }
  };

  debouncedFunc: (fn: () => void) => any = fn => {
    if (!this.debounce) {
      this.debounce = debounce(fn, 1000);
    }
    return this.debounce;
  };

  getResults: () => void = () => {
    this.currentBounce = this.debouncedFunc(() => {
      const query = getQueryString(this.props.form);
      this.props.dispatch({
        type: Actions.FETCH_RESULTS,
        query,
        currentUrl: '/car-leasing-deals'
      });
      this.debounce = null;
    });
    this.currentBounce();
  };

  toggleFurtherOptions: () => void = () => {
    const isFilterFormOpen = this.state.showFurtherOptions;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: !isFilterFormOpen ? 'More options' : 'Less options'
      }
    });
    if (
      this.props.isMobile &&
      typeof window !== 'undefined' &&
      window.document &&
      document.body
    ) {
      document.body.style.overflow = isFilterFormOpen ? 'auto' : 'hidden';
      if (isFilterFormOpen) {
        setAutocarHeaderVisibility(true);
      } else {
        setAutocarHeaderVisibility(false);
      }
    }
    this.setState(
      {
        showFurtherOptions: !isFilterFormOpen
      },
      () => (!isFilterFormOpen ? scrollToTopOfPage() : null)
    );
  };

  /**
   * resetField.
   * @param {string} field - field
   *
   * @return {void}
   */
  resetField: (field: string) => void = field => {
    if (field === 'nearlynew' || field === 'used') {
      this.props.dispatch(change('search', field, true));
    } else {
      this.props.dispatch(change('search', field, null));
    }

    if (field === 'make') {
      this.props.dispatch(change('search', 'model', null));
    }
  };

  removeActiveFilter: (field: string) => void = key => {
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Remove Active Filter'
      }
    });
    this.resetField(key);
  };

  resetAllFields(fields: any) {
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Clear all'
      }
    });
    R.keys(fields).forEach(field => {
      this.resetField(field);
    });
    if (fields) {
      this.getResults();
    }
  }

  /**
   * Renders the Home component.
   *
   * @return {JSX} - rendered Home page.
   */
  render(): Element<*> {
    const {
      makes,
      models,
      results,
      form,
      priceFrom,
      priceTo,
      additonalOptions,
      isResultsLoading,
      isUILoading,
      isError,
      popularCars,
      isMobile
    } = this.props;

    const isLeasingPage = thisPageData.name === 'car-leasing-deals';

    const resultsLength =
      results && results.pagination ? results.pagination.total : 0;
    const fields = form ? { ...form.values } : {};
    const activeFilters = form ? createActiveFields(form.values) : [];

    const adProps = {
      page: adUnitPageNames['home']
    };

    const carouselItemsToDisplay = { xs: 1, sm: 2, md: 2, lg: 3 };
    const classifiedsSite = getClassifiedsSite();
    console.log(popularCars, 'popularCars');
    return (
      <Fragment>
        {typeof window !== 'undefined' &&
          window.document &&
          (isMobile && this.state.showFurtherOptions ? (
            <Fragment>
              <div
                className={styles.filterFormBackdrop}
                onClick={this.toggleFurtherOptions}
              ></div>
              <div
                className={classNames(
                  styles.filterForm,
                  fields && styles.isVisible
                )}
              >
                <FilterForm
                  initialValues={fields}
                  onSubmit={this.submit}
                  getResults={this.getResults}
                  fields={fields}
                  activeFilters={activeFilters || []}
                  makes={makes}
                  models={models}
                  priceFrom={priceFrom}
                  priceTo={priceTo}
                  additonalOptions={additonalOptions}
                  fetchUpdatedQuery={() => {}}
                  resultsLength={resultsLength}
                  isMobile={isMobile}
                  removeActiveFilter={this.removeActiveFilter}
                  resetAllFields={() => this.resetAllFields(fields)}
                  resetField={this.resetField}
                  isListingsPage={false}
                  closeFilters={this.toggleFurtherOptions}
                  isResultsLoading={isResultsLoading}
                  isLeasingPage={isLeasingPage}
                />
              </div>
            </Fragment>
          ) : (
            <SearchForm
              onSubmit={this.submit}
              getResults={this.getResults}
              fields={fields}
              makes={makes}
              models={models}
              additonalOptions={additonalOptions}
              resultsLength={resultsLength}
              toggleFurtherOptions={this.toggleFurtherOptions}
              showFurtherOptions={this.state && this.state.showFurtherOptions}
              priceFrom={priceFrom}
              priceTo={priceTo}
              isResultsLoading={isResultsLoading}
              isUILoading={isUILoading}
              isMobile={isMobile}
              resetAllFields={() => this.resetAllFields(fields)}
              thisPageData={thisPageData}
              isLeasingPage={isLeasingPage}
            />
          ))}
        <Grid>
          <Row>
            <Col colXs={12} colSm={12} colMd={12} colLg={12}>
              <QuickLinks quickLinksData={quickLinksData} />
            </Col>
          </Row>
        </Grid>

        <Grid>
          <SEO {...metadata.home(pageType)} />
          {isError && (
            <Row>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <ErrorBox type={500} />
              </Col>
            </Row>
          )}
          {!isError && !isMobile && !this.state.delayAdMasthead && (
            <Row>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <div className={styles.headerAd}>
                  <GPT
                    id={slots.header.id}
                    adUnitPath={createAdUnitPath(adProps)}
                    collapseEmptyDiv={true}
                    renderWhenViewable={false}
                    sizeMapping={slots.header.sizeMapping}
                    targeting={slots.header.targeting}
                  />
                </div>
              </Col>
            </Row>
          )}
          {!isError && isMobile && (
            <Row>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <div
                  className={classNames(styles.headerAd, styles.headerAdSticky)}
                >
                  <GPT
                    id={slots.banner.id}
                    adUnitPath={createAdUnitPath(adProps)}
                    collapseEmptyDiv={true}
                    renderWhenViewable={false}
                    sizeMapping={slots.banner.sizeMapping}
                    targeting={slots.banner.targeting}
                  />
                </div>
              </Col>
            </Row>
          )}
          {!isError && (
            <Row>
              <Col colXs={12} colSm={12} colMd={9} colLg={9}>
                <section className={styles.intro}>
                  <h3 className={styles.heading}>
                    {homeText[classifiedsSite]['title']}
                  </h3>
                  <p className={styles.p}>
                    {homeText[classifiedsSite]['description']}
                  </p>
                </section>
                {!isError && isMobile && (
                  <div className={styles.sideAd}>
                    <GPT
                      id={slots.topMpu.id}
                      adUnitPath={createAdUnitPath(adProps)}
                      collapseEmptyDiv={true}
                      renderWhenViewable={false}
                      sizeMapping={slots.topMpu.sizeMapping}
                      targeting={slots.topMpu.targeting}
                    />
                  </div>
                )}
                {!isError && popularCars && (
                  <CardsCarousel
                    carouselItemsToDisplay={carouselItemsToDisplay}
                    extendStyle={styles.classDetailCarouselItems}
                    title={'Popular cars'}
                    history={this.props.history}
                    related={isMobile ? popularCars.slice(0, 10) : popularCars}
                    gtmSectionName={'popularCars'}
                    dataBi="autotrader-click"
                    dataSection="popular-cars"
                    isLeasingPage={isLeasingPage}
                    utm="CARLEASINGCAROUSELLINKS"
                  />
                )}
              </Col>
              <Col colXs={12} colSm={12} colMd={3} colLg={3}>
                <div className={styles.sideAd}>
                  {!isError && !isMobile && (
                    <GPT
                      id={slots.flexible.id}
                      adUnitPath={createAdUnitPath(adProps)}
                      collapseEmptyDiv={true}
                      renderWhenViewable={false}
                      sizeMapping={slots.flexible.sizeMapping}
                      targeting={slots.flexible.targeting}
                    />
                  )}
                  {!isError && isMobile && (
                    <React.Fragment>
                      {!this.state.delayAdOverlay && (
                        <GPT
                          id={slots.overlayMobile.id}
                          adUnitPath={createAdUnitPath(adProps)}
                          collapseEmptyDiv={true}
                          renderWhenViewable={false}
                          sizeMapping={slots.overlayMobile.sizeMapping}
                          targeting={slots.overlayMobile.targeting}
                          outOfPage={slots.overlayMobile.outOfPage}
                        />
                      )}

                      {!this.state.delayAdOverlay2 && (
                        <GPT
                          id={slots.overlay2Mobile.id}
                          adUnitPath={createAdUnitPath(adProps)}
                          collapseEmptyDiv={true}
                          renderWhenViewable={false}
                          sizeMapping={slots.overlay2Mobile.sizeMapping}
                          targeting={slots.overlay2Mobile.targeting}
                        />
                      )}
                    </React.Fragment>
                  )}
                  {!isError && !isMobile && (
                    <React.Fragment>
                      {!this.state.delayAdOverlay && (
                        <GPT
                          id={slots.overlay.id}
                          adUnitPath={createAdUnitPath(adProps)}
                          collapseEmptyDiv={true}
                          renderWhenViewable={false}
                          sizeMapping={slots.overlay.sizeMapping}
                          targeting={slots.overlay.targeting}
                          outOfPage={slots.overlay.outOfPage}
                        />
                      )}

                      {!this.state.delayAdOverlay2 && (
                        <GPT
                          id={slots.overlay2.id}
                          adUnitPath={createAdUnitPath(adProps)}
                          collapseEmptyDiv={true}
                          renderWhenViewable={false}
                          sizeMapping={slots.overlay2.sizeMapping}
                          targeting={slots.overlay2.targeting}
                        />
                      )}
                    </React.Fragment>
                  )}
                </div>
              </Col>
            </Row>
          )}
          {!isError && (
            <Row>
              <Col colXs={12} className={styles.mtl}>
                <Accordion
                  accordionHeight={200}
                  gtmSectionName={'accordionBrowse'}
                >
                  <BrowseAll
                    items={makes}
                    text="makes"
                    history={this.props.history}
                    thisPageData={thisPageData}
                  />
                </Accordion>
              </Col>
            </Row>
          )}
        </Grid>
      </Fragment>
    );
  }
}

// $FlowFixMe
export default connect(Home.mapStateToProps)(Home);



// WEBPACK FOOTER //
// ./src/leasingCars/Home/index.js